import { useEffect, useState } from "react";
import Form from "../../../Components/Form";
import FileInput from "../../../Components/FileInput/FileInput";
import DownloadOrderTemplate from "../../../Components/DownloadOrderTemplate";
import Button from "../../../Components/Button";
import useRequest from "../../../Hooks/useRequest";
import { toast } from "react-toastify";
import { CommonTableUI } from "../../../Tables/TableTemplate/common";
import { ErrorTableReport } from "../../../Interfaces/orderInterfaces";
import SahraConnections from "../../Sahra/SahraConnections";
import Modal from "../../../Components/Modal/Modal";
import { Report } from "../../../Interfaces/TableInterfaces";
import FileBulkOrderTableUI from "../../../Tables/Order/FileBulkOrder/FileBulkOrderTableUI";
import Loading from "../../../Components/Loading/Loading";
import {
  addDeltaHedgeOrderBulkOrder,
  uploadDeltaOrderFile,
} from "../../../Requests/deltaHedgeOrder";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import { IBulkOrderRow } from "../../../Interfaces/deltaHedgeOrder";
import OrderPreviewTable, { isDeltaHedgePreviewRowSendable } from "../OrderPreviewTable";

enum ModalMode {
  BulkOrderPreview,
  BulkOrderUsers,
  BulkOrderResponseTable,
}

const BulkOrder = () => {
  const [excelFile, setExcelFile] = useState<FormData | null>(null);
  const [bulkOrderRows, setBulkOrderRows] =
    useState<Array<IBulkOrderRow> | null>(null);
  const [uploadFileErrorTableData, setUploadFileErrorTableData] =
    useState<Report<ErrorTableReport> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(ModalMode.BulkOrderPreview);

  function errorHandler(e: any) {
    if (e.response.status === 400) {
      setUploadFileErrorTableData(e.response.data);
      setIsModalOpen(true);
      setModalMode(ModalMode.BulkOrderPreview);
    }

    if (e.response.status === 500) toast.error("خطا داخلی");
  }

  const addBulkOrderRequest = useRequest(
    () => addDeltaHedgeOrderBulkOrder(bulkOrderRows),
    packetErrorHandler
  );

  const fileOrderRequest = useRequest(
    () => uploadDeltaOrderFile(excelFile),
    errorHandler
  );

  const bulkOrderClickHandler = () => {
    if (fileOrderRequest.loading) return;
    fileOrderRequest.fetchData();
  };

  const getOrderRows = (reports: Array<any>) => {
    setBulkOrderRows(reports.map((report) => report.orderRow).filter(isDeltaHedgePreviewRowSendable));
  };

  useEffect(() => {
    if (fileOrderRequest.response) {
      setIsModalOpen(true);
      setModalMode(ModalMode.BulkOrderPreview);
      getOrderRows(fileOrderRequest.response.report);
      setUploadFileErrorTableData(null);
    }
  }, [fileOrderRequest.response]);

  const getBulkOrderPreviewTable = () => {
    if (uploadFileErrorTableData)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          <div className="border-2 border-white border-b-red-700 text-red-700 w-full text-center mb-3">
            خطا
          </div>
          <CommonTableUI reports={uploadFileErrorTableData} />
        </div>
      );
    if (fileOrderRequest.response)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          <OrderPreviewTable values={fileOrderRequest.response.report.map((report: any) => ({ ...report.orderRow, accessMessage: report.accessMessage || report.orderRow?.accessMessage }))} />
          <div className="mt-3 w-full">
            <Button
              onClick={() => setModalMode(ModalMode.BulkOrderUsers)}
              text="ارسال"
              variant="contained"
              disabled={!bulkOrderRows?.length}
            />
          </div>
        </div>
      );
    else return <></>;
  };

  const getBulkOrderSahraConnectionsModalUI = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-3">
        <SahraConnections initialSearchParam={{ userIds: getUserIds() }} />
        <Button
          onClick={bulkOrderSahraConnectionsModalBtnClickHandler}
          text="ارسال"
          variant="contained"
          loading={addBulkOrderRequest.loading}
        />
      </div>
    );
  };

  const getFileBulkOrderTableUI = () => {
    if (addBulkOrderRequest.response)
      return <FileBulkOrderTableUI reports={addBulkOrderRequest.response} />;
    if (addBulkOrderRequest.loading)
      return (
        <div className="flex justify-center items-center w-full h-60">
          <Loading className="!text-sky-500" />
        </div>
      );
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview:
        return getBulkOrderPreviewTable();

      case ModalMode.BulkOrderUsers:
        return getBulkOrderSahraConnectionsModalUI();

      case ModalMode.BulkOrderResponseTable:
        return getFileBulkOrderTableUI();
    }
  };

  const getUserIds = () => {
    if (!bulkOrderRows) return [];
    const res: number[] = [];

    bulkOrderRows.forEach((row) => {
      res.push(row.accountId);
    });
    return res;
  };

  const bulkOrderSahraConnectionsModalBtnClickHandler = () => {
    addBulkOrderRequest.fetchData();
    setModalMode(ModalMode.BulkOrderResponseTable);
  };

  const modalCloseHandler = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview: {
        setIsModalOpen(false);
        break;
      }
      case ModalMode.BulkOrderUsers: {
        setModalMode(ModalMode.BulkOrderPreview);
        break;
      }
      case ModalMode.BulkOrderResponseTable: {
        setIsModalOpen(false);
        break;
      }

      default:
        break;
    }
  };

  return (
    <Form>
      {isModalOpen ? (
        <Modal title="ارسال دسته‌ای" modalBoxClassName="!min-w-0 w-[1320px]" onClose={modalCloseHandler} UI={getModalUI()} />
      ) : (
        <></>
      )}
      <div className="p-2 w-full">
        <div className="bold mb-2 text-xs">ارسال دسته ای</div>
        <div className="flex flex-wrap items-center gap-3">
          <FileInput
            fileExtension=".xlsx, .xls, .csv"
            UI={<></>}
            onChange={setExcelFile}
            value={excelFile}
            name="formFile"
            label="انتخاب فایل اکسل"
          />
          <DownloadOrderTemplate kind="delta-hedge" />
        </div>
        <div className="mt-3 w-full">
          <Button
            text="ارسال"
            onClick={bulkOrderClickHandler}
            variant="contained"
            disabled={!excelFile || fileOrderRequest.loading}
            loading={fileOrderRequest.loading}
          />
        </div>
      </div>
    </Form>
  );
};

export default BulkOrder;
